import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ILocalStore } from '@/stores/interfaces/ILocalStore';

type StreamingStatusStoreParams = {
  getHasReceivedToken: () => boolean;
  onStatusShown?: (status: StreamingStatusItem) => void;
  onQueueEmpty?: () => void;
};

type StreamingStatusKind = 'call' | 'result' | 'status';

export type StreamingStatusItem = {
  text: string;
  kind?: StreamingStatusKind;
};

type PrivateFields = '_statusText' | '_statusLines';

const ROTATION_DELAY_MS = 2000;

export class StreamingStatusStore implements ILocalStore {
  private readonly _getHasReceivedToken: () => boolean;
  private readonly _onStatusShown?: (status: StreamingStatusItem) => void;
  private readonly _onQueueEmpty?: () => void;
  private _statusText: StreamingStatusItem | null = null;
  private _statusLines: StreamingStatusItem[] = [];
  private _statusTimer: ReturnType<typeof setTimeout> | null = null;
  private _queueEmptyTimer: ReturnType<typeof setTimeout> | null = null;
  private _hasQueueEmptyNotified = false;

  constructor({ getHasReceivedToken, onStatusShown, onQueueEmpty }: StreamingStatusStoreParams) {
    this._getHasReceivedToken = getHasReceivedToken;
    this._onStatusShown = onStatusShown;
    this._onQueueEmpty = onQueueEmpty;

    makeObservable<StreamingStatusStore, PrivateFields>(this, {
      _statusText: observable.ref,
      _statusLines: observable.shallow,
      statusText: computed,
      hasQueuedStatuses: computed,
      setText: action.bound,
      clearLines: action.bound,
      append: action.bound,
      scheduleRotation: action.bound,
      clearRotation: action.bound,
      reset: action.bound,
    });
  }

  get statusText(): string | null {
    return this._statusText?.text ?? null;
  }

  get hasQueuedStatuses(): boolean {
    return this._statusLines.length > 0;
  }

  private _notifyStatusShown(status: StreamingStatusItem): void {
    if (!this._onStatusShown) {
      return;
    }

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => this._onStatusShown?.(status));
    } else {
      setTimeout(() => this._onStatusShown?.(status), 0);
    }
  }

  private _notifyQueueEmptyOnce(): void {
    // Не даём onQueueEmpty сработать повторно, иначе "Собрал всю информацию..." появится несколько раз.
    if (!this._onQueueEmpty || this._hasQueueEmptyNotified) {
      return;
    }

    this._hasQueueEmptyNotified = true;

    setTimeout(() => this._onQueueEmpty?.(), 0);
  }

  setText(text: string | null): void {
    this._statusText = text ? { text } : null;
  }

  clearLines(): void {
    this._statusLines = [];
  }

  append(text: string, kind?: StreamingStatusKind): void {
    this.clearRotation();
    this._hasQueueEmptyNotified = false;
    const lastQueued = this._statusLines[this._statusLines.length - 1];
    const nextStatus: StreamingStatusItem = { text, kind };

    if (
      (this._statusText && this._statusText.text === nextStatus.text && this._statusText.kind === nextStatus.kind) ||
      (lastQueued && lastQueued.text === nextStatus.text && lastQueued.kind === nextStatus.kind)
    ) {
      this._notifyStatusShown(nextStatus);

      return;
    }

    // Записываем шаг сразу, независимо от ротации статусов.
    this._notifyStatusShown(nextStatus);

    const shouldTrackQueueEmpty = !kind || kind === 'call' || kind === 'result';

    // Отслеживаем период без новых tool_call после последнего tool_call/tool_call_result.
    if (shouldTrackQueueEmpty) {
      this._scheduleQueueEmpty();
    } else if (this._queueEmptyTimer != null) {
      clearTimeout(this._queueEmptyTimer);
      this._queueEmptyTimer = null;
    }

    if (this._getHasReceivedToken()) {
      return;
    }

    if (this._statusText) {
      this._statusLines = [...this._statusLines, nextStatus];
    } else {
      this._statusText = nextStatus;
    }

    this.scheduleRotation();
  }

  scheduleRotation(): void {
    // Ротация отвечает только за отображение статусов в UI.
    if (this._statusTimer != null) {
      return;
    }

    if (this._statusLines.length === 0) {
      if (!this._statusText) {
        return;
      }

      this._statusTimer = setTimeout(() => {
        runInAction(() => {
          this._statusTimer = null;

          if (this._statusLines.length === 0 && this._statusText) {
            // Очередь статусов закончилась — можно добавить финальный шаг.
            this._notifyQueueEmptyOnce();
          }
        });
      }, ROTATION_DELAY_MS);

      return;
    }

    this._statusTimer = setTimeout(() => {
      runInAction(() => {
        const [nextStatus, ...rest] = this._statusLines;

        this._statusLines = rest;

        if (nextStatus) {
          this._statusText = nextStatus;
        }

        this._statusTimer = null;
        this.scheduleRotation();
      });
    }, ROTATION_DELAY_MS);
  }

  clearRotation(): void {
    if (this._statusTimer != null) {
      clearTimeout(this._statusTimer);
      this._statusTimer = null;
    }
  }

  private _scheduleQueueEmpty(): void {
    // Таймер периода без новых tool_call — запускается после каждого tool_call/result.
    if (this._queueEmptyTimer != null) {
      clearTimeout(this._queueEmptyTimer);
    }

    this._queueEmptyTimer = setTimeout(() => {
      runInAction(() => {
        this._queueEmptyTimer = null;
        this._notifyQueueEmptyOnce();
      });
    }, ROTATION_DELAY_MS);
  }

  reset(): void {
    this._statusText = null;
    this._statusLines = [];
    this._hasQueueEmptyNotified = false;
    if (this._queueEmptyTimer != null) {
      clearTimeout(this._queueEmptyTimer);
      this._queueEmptyTimer = null;
    }
    this.clearRotation();
  }

  destroy(): void {
    if (this._queueEmptyTimer != null) {
      clearTimeout(this._queueEmptyTimer);
      this._queueEmptyTimer = null;
    }
    this.clearRotation();
  }
}
